import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useApiQuery, useToast } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import { extractApiErrorMessage } from '../../api/errors'
import { useCreditGate } from '../../payment/useCreditGate'
import { CreditOverdueBanner, GatedSaveButton } from '../../payment/CreditGateNotice'
import { LogicOperandPicker } from '../../rules/LogicOperandPicker'
import { STATUS_OPTIONS } from '../../rules/momentumStatus'
import { webhookVariableHelp } from '../../rules/webhookVariables'
import {
  LOGIC_OPTIONS,
  emptyLogicOperand,
  generateConditionText,
  logicOperandToString,
  parseLogicOperand,
  type LogicOperand,
} from '../../rules/logicOperand'
import type { CoinPairList, IndicatorCondition, IndicatorDescriptor, Momentum, Webhook } from '../../api/types'

const EMPTY_WEBHOOK: Webhook = { url: '', method: 'POST', format: 'json', data: '' }

// Backend default for a NEW row is `source: 'cl'` (closed price) —
// `api.cryptopairscreener.com`'s `app/momentum/store.py::create_momentum`.
// Legacy's edit-page default of `'cd'` (`momentum.source = momentum.source ||
// 'cd'`) only ever applied to an already-loaded row that came back with no
// `source` at all, which the backend no longer does (every read now includes
// one).
const EMPTY: Momentum = {
  id: null,
  exchangeId: '',
  tradingType: '',
  name: '',
  status: 1,
  indicator: '',
  source: 'cl',
  logic: 'gt',
  logicOperand1: '',
  logicOperand2: '',
  coinPairListId: '',
  indicatorConditionId: '',
  intervals: [],
  coinPairs: [],
  webhook: EMPTY_WEBHOOK,
}

// `WEBHOOK_METHODS`/`WEBHOOK_FORMATS` — `api.cryptopairscreener.com`'s
// `app/momentum/webhook.py`. A save with any other value is a 422
// (`unknown_method`/`unknown_format`).
const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const WEBHOOK_FORMATS: { value: string; label: string }[] = [
  { value: 'json', label: 'JSON' },
  { value: 'form', label: 'Form data' },
  { value: 'raw', label: 'Raw' },
]
const NO_BODY_METHODS = new Set(['GET', 'DELETE'])

/**
 * Equivalent of legacy `MomentumEditComponent` (audit §6 — create/edit a
 * momentum alert rule). A momentum either references a shared
 * `IndicatorCondition` (`indicatorConditionId` set — the condition is edited
 * on its own page and shown here as read-only text via
 * `generateConditionText`) or defines its own inline condition directly
 * (`logic`/`logicOperand1`/`logicOperand2`, edited via the ported
 * `LogicOperandPicker` — legacy's `!momentum.indicatorConditionId` branch).
 * Credit-balance edit gating matches legacy exactly (`useCreditGate`).
 *
 * B6 (BubbleTicket 1acfddcb, port plan §7): parity for everything that
 * actually controls a user's bot — status, source, the webhook editor, and a
 * custom coin list when no `CoinPairList` is chosen. None of this existed in
 * round 1 (`MomentumEditPage.tsx:139-219` had no status/source/webhook
 * fields, and the coin-pair-list `<select>` was `required`, making "custom
 * list" — the empty selection — impossible to reach).
 */
export function MomentumEditPage() {
  const { exchangeId = '', tradingType = '', id = 'new' } = useParams<{
    exchangeId: string
    tradingType: string
    id: string
  }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const { showToast } = useToast()
  const gate = useCreditGate()
  const [momentum, setMomentum] = useState<Momentum>({ ...EMPTY, exchangeId, tradingType })
  const [intervalsText, setIntervalsText] = useState('')
  const [logicOperand1, setLogicOperand1] = useState<LogicOperand>(emptyLogicOperand())
  const [logicOperand2, setLogicOperand2] = useState<LogicOperand>(emptyLogicOperand())
  const [newCoinPair, setNewCoinPair] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const existing = useApiQuery(
    () => (isNew ? Promise.resolve(null) : screenerClient.viewMomentum(exchangeId, tradingType, id)),
    [exchangeId, tradingType, id],
  )
  const coinpairlists = useApiQuery(() => screenerClient.searchCoinpairlists(exchangeId, tradingType), [exchangeId, tradingType])
  const indicatorconditions = useApiQuery(
    () => screenerClient.searchIndicatorconditions(exchangeId, tradingType),
    [exchangeId, tradingType],
  )
  // Webhook variable-help table (legacy `momentum-edit.component.html:494-510`
  // — the custom `@<TextData.name>@` rows).
  const textdata = useApiQuery(() => screenerClient.searchTextdata(), [])
  // Field-name list for the "indicator" operand option, resolved the same
  // way legacy's `buildIndicatorConfig()` did (`'-'`/`'-'` coin placeholders
  // — the field list doesn't depend on a coin pair). Legacy picked
  // `searchIndicators` (live candle) vs `searchClosedIndicators` (closed
  // price) based on `momentum.source`.
  const indicatorList = useApiQuery(
    () =>
      momentum.source === 'cd'
        ? screenerClient.searchIndicators(exchangeId, tradingType, '-', '-')
        : screenerClient.searchClosedIndicators(exchangeId, tradingType, '-', '-'),
    [exchangeId, tradingType, momentum.source],
  )

  useEffect(() => {
    if (existing.data) {
      setMomentum({ ...existing.data, webhook: existing.data.webhook ?? EMPTY_WEBHOOK, coinPairs: existing.data.coinPairs ?? [] })
      setIntervalsText((existing.data.intervals ?? []).join(','))
      setLogicOperand1(parseLogicOperand(existing.data.logicOperand1))
      setLogicOperand2(parseLogicOperand(existing.data.logicOperand2))
    }
  }, [existing.data])

  const possibleFields = useMemo(() => {
    const match = (indicatorList.data?.byIndicator ?? []).find(
      (ind: IndicatorDescriptor) => ind.indicator === momentum.indicator,
    )
    return match?.fields ?? []
  }, [indicatorList.data, momentum.indicator])

  // "Add coin" options for the custom coin-pair list (legacy `possibleCoinPairs`,
  // built from the selected indicator's `coinPairs`, `momentum-edit.component.ts`
  // `buildIndicatorConfig()`) — `{id: "BTC_USDT", label: "BTCUSDT"}`.
  const possibleCoinPairs = useMemo(() => {
    const match = (indicatorList.data?.byIndicator ?? []).find(
      (ind: IndicatorDescriptor) => ind.indicator === momentum.indicator,
    )
    return (match?.coinPairs ?? []).map((pair) => ({ id: pair, label: pair.replace('_', '') }))
  }, [indicatorList.data, momentum.indicator])

  function update<K extends keyof Momentum>(key: K, value: Momentum[K]) {
    setMomentum((prev) => ({ ...prev, [key]: value }))
  }

  function updateWebhook<K extends keyof Webhook>(key: K, value: Webhook[K]) {
    setMomentum((prev) => ({ ...prev, webhook: { ...(prev.webhook ?? EMPTY_WEBHOOK), [key]: value } }))
  }

  function changeIndicator(value: string) {
    // Legacy `indicatorChanged()`: switching indicators resets both operands
    // since the previous `field`/`index` choices no longer apply.
    setMomentum((prev) => ({ ...prev, indicator: value }))
    setLogicOperand1(emptyLogicOperand())
    setLogicOperand2(emptyLogicOperand())
  }

  function addCoinPair() {
    if (!newCoinPair) return
    setMomentum((prev) => {
      const pairs = prev.coinPairs ?? []
      if (pairs.includes(newCoinPair)) return prev
      return { ...prev, coinPairs: [...pairs, newCoinPair] }
    })
    setNewCoinPair('')
  }

  function removeCoinPair(pair: string) {
    setMomentum((prev) => ({ ...prev, coinPairs: (prev.coinPairs ?? []).filter((p) => p !== pair) }))
  }

  const webhook = momentum.webhook ?? EMPTY_WEBHOOK
  const webhookHasBody = !NO_BODY_METHODS.has((webhook.method || 'GET').toUpperCase())
  // Legacy `WebhookComponent.checkData()`: a visual-only warning (never
  // blocks Save) that the webhook data isn't valid JSON. The backend only
  // actually validates the RENDERED data at webhook-fire time
  // (`app/momentum/webhook.py::render_webhook`), not at save time.
  const webhookDataIsValid = !webhook.data || (() => {
    try {
      JSON.parse(webhook.data)
      return true
    } catch {
      return false
    }
  })()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setSaveError(null)
    const usesInlineCondition = !momentum.indicatorConditionId
    const payload: Partial<Momentum> = {
      ...momentum,
      intervals: intervalsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      ...(usesInlineCondition
        ? { logicOperand1: logicOperandToString(logicOperand1), logicOperand2: logicOperandToString(logicOperand2) }
        : {}),
    }
    try {
      if (isNew) {
        const created = await screenerClient.createMomentum(exchangeId, tradingType, payload)
        showToast('Momentum created', 'success')
        navigate(`/${exchangeId}/${tradingType}/momentums/${created.id}`)
      } else {
        await screenerClient.modifyMomentum(exchangeId, tradingType, id, payload)
        showToast('Momentum saved', 'success')
      }
    } catch (err) {
      const message = extractApiErrorMessage(err, 'Could not save momentum')
      setSaveError(message)
      showToast(message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <h1>{isNew ? 'New momentum' : `Edit momentum: ${momentum.name}`}</h1>

      <CreditOverdueBanner gate={gate} itemLabel="momentums" />

      {saveError && (
        <div className="screener-alert screener-alert-danger" role="alert">
          {saveError}
        </div>
      )}

      <form className="screener-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input value={momentum.name} onChange={(e) => update('name', e.target.value)} required />
        </label>

        <label>
          Status
          <select value={momentum.status ?? 1} onChange={(e) => update('status', Number(e.target.value))}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Source
          <select value={momentum.source ?? 'cl'} onChange={(e) => update('source', e.target.value)}>
            <option value="cd">Candle (live)</option>
            <option value="cl">Closed price</option>
          </select>
        </label>

        <label>
          Indicator
          <input value={momentum.indicator} onChange={(e) => changeIndicator(e.target.value)} required />
        </label>

        <label>
          Indicator condition
          <select value={momentum.indicatorConditionId} onChange={(e) => update('indicatorConditionId', e.target.value)}>
            <option value="">None (define condition below)</option>
            {(indicatorconditions.data ?? []).map((ic: IndicatorCondition) => (
              <option key={ic.id ?? ic.name} value={ic.id ?? ''}>
                {ic.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Coin-pair list
          <select value={momentum.coinPairListId} onChange={(e) => update('coinPairListId', e.target.value)}>
            <option value="">Custom list</option>
            {(coinpairlists.data ?? []).map((cpl: CoinPairList) => (
              <option key={cpl.id ?? cpl.name} value={cpl.id ?? ''}>
                {cpl.name}
              </option>
            ))}
          </select>
        </label>

        {momentum.coinPairListId ? (
          <div className="screener-card">
            <strong>Coin pairs (from the linked list)</strong>
            <ul>
              {(momentum.coinPairs ?? []).map((pair) => (
                <li key={pair}>{pair.replace('_', ' / ')}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="screener-card">
            <strong>Coin pairs (custom list)</strong>
            <ul>
              {(momentum.coinPairs ?? []).map((pair) => (
                <li key={pair}>
                  {pair.replace('_', ' / ')}{' '}
                  <button type="button" onClick={() => removeCoinPair(pair)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="screener-actions">
              <select aria-label="Add coin pair" value={newCoinPair} onChange={(e) => setNewCoinPair(e.target.value)}>
                <option value="">Add new coin …</option>
                {possibleCoinPairs
                  .filter((coin) => !(momentum.coinPairs ?? []).includes(coin.id))
                  .map((coin) => (
                    <option key={coin.id} value={coin.id}>
                      {coin.label}
                    </option>
                  ))}
              </select>
              <button type="button" disabled={!newCoinPair} onClick={addCoinPair}>
                Add coin
              </button>
            </div>
          </div>
        )}

        <div className="form-group">
          <strong>Condition</strong>
          {momentum.indicatorConditionId ? (
            <p>{generateConditionText(momentum)}</p>
          ) : (
            <>
              <LogicOperandPicker
                operand={logicOperand1}
                onChange={setLogicOperand1}
                indicatorName={momentum.indicator}
                possibleFields={possibleFields}
                source={momentum.source}
              />
              <label>
                Logic
                <select value={momentum.logic ?? 'gt'} onChange={(e) => update('logic', e.target.value)}>
                  <option value="">choose …</option>
                  {LOGIC_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <LogicOperandPicker
                operand={logicOperand2}
                onChange={setLogicOperand2}
                indicatorName={momentum.indicator}
                possibleFields={possibleFields}
                source={momentum.source}
              />
            </>
          )}
        </div>

        <label>
          Intervals (comma-separated, e.g. 1h,4h,1d)
          <input value={intervalsText} onChange={(e) => setIntervalsText(e.target.value)} />
        </label>

        <fieldset className="screener-card">
          <legend>
            <strong>Webhook</strong>
          </legend>

          <label>
            Method
            <select value={webhook.method || 'POST'} onChange={(e) => updateWebhook('method', e.target.value)}>
              {WEBHOOK_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label>
            Url
            <input value={webhook.url} onChange={(e) => updateWebhook('url', e.target.value)} placeholder="https://…" />
          </label>
          <p className="screener-text-info">
            Bot credentials (user/password) must go in the request body — never the URL. The backend
            refuses a webhook URL containing credentials (query params such as <code>user</code>/
            <code>pass</code>/<code>token</code>, or userinfo in the URL itself).
          </p>

          {webhookHasBody && (
            <>
              <label>
                Request data (JSON)
                <textarea
                  rows={8}
                  className={webhookDataIsValid ? '' : 'screener-input-invalid'}
                  value={webhook.data}
                  onChange={(e) => updateWebhook('data', e.target.value)}
                  aria-invalid={!webhookDataIsValid}
                />
              </label>
              {!webhookDataIsValid && (
                <p className="screener-alert-danger" role="alert">
                  This is not valid JSON.
                </p>
              )}

              <label>
                Request data format
                <select value={webhook.format || 'json'} onChange={(e) => updateWebhook('format', e.target.value)}>
                  {WEBHOOK_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          <div className="screener-card">
            <strong>Possible variables for the url and data fields:</strong>
            <table className="screener-webhook-vars">
              <tbody>
                {webhookVariableHelp(textdata.data ?? []).map((v) => (
                  <tr key={v.token}>
                    <td>
                      <strong>{v.token}</strong>
                    </td>
                    <td>{v.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset>

        <GatedSaveButton gate={gate} saving={saving} />
      </form>
    </div>
  )
}
